"use client";

import { useState } from "react";
import { readBrowserAttribution } from "@/components/analytics/browser-attribution";

interface GuideLeadCaptureProps {
  headline: string;
  type: "guide_download" | "newsletter";
}

export function GuideLeadCapture({ headline, type }: GuideLeadCaptureProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || status === "submitting") return;

    setStatus("submitting");
    try {
      const res = await fetch("/api/lead-magnet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, type, attribution: readBrowserAttribution() }),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-xl border border-attn-line bg-attn-bg p-6 sm:p-8">
        <p className="text-lg font-bold text-gray-900">Check your inbox!</p>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          We&rsquo;ll send it to{" "}
          <strong className="text-gray-900">{email}</strong> shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-attn-line bg-attn-bg p-6 sm:p-8">
      <h3 className="text-lg font-bold text-gray-900">{headline}</h3>
      <form onSubmit={handleSubmit} className="mt-4 flex gap-3 sm:max-w-md">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="min-w-0 flex-1 rounded-lg border border-attn-line bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-50"
        >
          {status === "submitting" ? "Sending\u2026" : "Send it free"}
        </button>
      </form>
      {status === "error" && (
        <p className="mt-2 text-sm text-danger">
          Something went wrong. Please try again.
        </p>
      )}
      <p className="mt-3 text-xs text-gray-500">
        No spam. Just the free resource. Unsubscribe any time.
      </p>
    </div>
  );
}
